// pages/Blockchain.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button,
  InputGroup, Dropdown, Spinner, Alert
} from 'react-bootstrap';
import ViewPhraseModal from '../components/modals/BlockchainModals/ViewPhraseModal';
import PhraseFormModal from '../components/modals/BlockchainModals/PhraseFormModal';
import DeletePhraseModal from '../components/modals/BlockchainModals/DeletePhraseModal';
import AddToInheritanceModal from '../components/modals/AddToInheritanceModal';
import Sidebar from '../components/dashboard/Sidebar';
import ListHeader from '../components/common/ListHeader';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import {
  fetchPhrases,
  viewPhrase,
  savePhrase,
  deletePhrase,
  generateUniquePhraseAlias,
} from '../services/blockchain.service';
import { useAuth } from '../contexts/AuthContext';
import { checkPaymentStatus, checkStorageLimits, formatFileSize, formatItemDate, removeTxtExtension } from '../services/api.service';
import InheritanceDropdownItem from '../components/common/InheritanceDropdownItem';
import PropertiesModal from '../components/common/PropertiesModal';
import { logEvent } from '../analytics';


import '../styles/pages/blockchain.css';
import '../styles/components/modals.css';
import { useRef } from 'react';

const Blockchain = () => {
  // State for blockchain phrases data
  const [phrases, setPhrases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhrases, setSelectedPhrases] = useState([]);
  const [sidebarToggled, setSidebarToggled] = useState(false);

  // State for search and sort
  const [searchQuery, setSearchQuery] = useState('');
  const [sortType, setSortType] = useState('none');

  // State for modals
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [multipleDeleteModalOpen, setMultipleDeleteModalOpen] = useState(false);
  const [inheritanceModalOpen, setInheritanceModalOpen] = useState(false);
  const [currentPhrase, setCurrentPhrase] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingToInheritance, setIsAddingToInheritance] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const dropdownRefs = useRef({});
  const cardRefs = useRef({});
  const ignoreNextClick = useRef(false);

  // Get auth context
  const { token, user } = useAuth();
  const { isAccountFrozen, showFrozenAlert } = usePaymentStatus();
  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  // Load blockchain phrases
  useEffect(() => {
    loadPhrases();
  }, [token]);

  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }

      const isInsideAnyDropdown = Object.values(dropdownRefs.current).some(
        ref => ref && ref.contains(event.target)
      );

      const isInsideAnyCard = Object.values(cardRefs.current).some(
        ref => ref && ref.contains(event.target)
      );

      if (!isInsideAnyDropdown && !isInsideAnyCard) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadPhrases = async (forceClearCache = false) => {
    setIsLoading(true);
    try {
      // Clear any relevant cached data if force refresh
      if (forceClearCache) {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('cache-bucket-blockchain')) {
            localStorage.removeItem(key);
          }
        });
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('cache-bucket-blockchain')) {
            sessionStorage.removeItem(key);
          }
        });
      }

      // Pass the forceClearCache flag to fetchPhrases
      const data = await fetchPhrases(token, forceClearCache);
      setPhrases(data);
    } catch (error) {
      console.error('Error loading blockchain phrases:', error);
      showAlert(`Error loading blockchain phrases: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Alert helper
  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Handle view phrase
  const handleViewPhrase = async (phrase, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      setIsLoading(true);
      const phraseData = await viewPhrase(phrase.name, token);
      setCurrentPhrase(phraseData);
      setViewModalOpen(true);
    } catch (error) {
      showAlert(`Error viewing phrase: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (phraseId, e) => {
    if (e) {
      e.stopPropagation();
    }

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Set a timeout for single click action
    const timeout = setTimeout(() => {
      // Toggle dropdown when clicking the card
      setOpenDropdownId(prevId => prevId === phraseId ? null : phraseId);
      setClickTimeout(null);
    }, 250); // 250ms delay to detect double-click

    setClickTimeout(timeout);

  };

  const handleCardDoubleClick = (phrase, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear the single-click timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Close dropdown if it's open
    setOpenDropdownId(null);

    // Execute the view action (same as clicking "View" in dropdown)
    handleViewPhrase(phrase, e)
  };

  // Handle edit phrase
  const handleEditPhrase = async (phrase, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setIsLoading(true);
      const phraseData = await viewPhrase(phrase.name, token);
      setCurrentPhrase(phraseData);
      setIsEditing(true);
      setFormModalOpen(true);

      // Check payment status in the background
      checkPhrasePermissions();
    } catch (error) {
      showAlert(`Error loading phrase for editing: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      // Select all filtered phrases
      filteredPhrases.forEach((phrase) => {
        if (!selectedPhrases.includes(phrase.name)) {
          setSelectedPhrases(prev => [...prev, phrase.name]);
        }
      });
    } else {
      // Deselect all phrases
      setSelectedPhrases([]);
    }
  };

  // Check if all items are selected
  const areAllPhrasesSelected = () => {
    if (filteredPhrases.length === 0) return false;
    return filteredPhrases.every(phrase => selectedPhrases.includes(phrase.name));
  };

  // Check if some (but not all) items are selected
  const areSomePhrasesSelected = () => {
    return selectedPhrases.length > 0 && !areAllPhrasesSelected();
  };

  // Handle add phrase
  const handleAddPhrase = () => {
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      return;
    }
    setCurrentPhrase(null);
    setIsEditing(false);
    setFormModalOpen(true);

    // Check permissions in the background
    checkPhrasePermissions();
  };

  const checkPhrasePermissions = async () => {
    try {
      // Check payment status
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
        showAlert('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.', 'warning');
        setFormModalOpen(false);
        return;
      }

      // Check storage limits
      const storageCheck = await checkStorageLimits(153600, token);
      if (!storageCheck.withinLimits) {
        showAlert(`Storage limit exceeded. You have ${formatFileSize(storageCheck.remainingSpace)} remaining.`, 'warning');
        setFormModalOpen(false);
        return;
      }
    } catch (error) {
      showAlert(`Error: ${error.message}`, 'danger');
      setFormModalOpen(false);
    }
  };

  // Handle delete phrase
  const handleDeletePhrase = (phrase, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentPhrase(phrase);
    setDeleteModalOpen(true);
  };

  // Handle add to inheritance
  const handleAddToInheritance = async (phrase, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsAddingToInheritance(true);
    try {
      // Check payment status first
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
             logEvent('inheritance_add_item', {
       item_type: 'secure_phrase',
       item_alias: phrase?.name || 'unknown',
       add_result: 'blocked_overdue'
     });
        showAlert('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.', 'warning');
        return;
      }

      // Import the function from blockchain service
      const { addPhraseToCurrentInheritance } = await import('../services/blockchain.service');

      // Add the phrase directly to the current inheritance
      await addPhraseToCurrentInheritance(phrase.name, token);

         logEvent('inheritance_add_item', {
     item_type: 'secure_phrase',
     item_alias: phrase?.name || 'unknown',
     add_result: 'success'
   });

      showAlert('Secure phrase successfully added to endowment', 'success');
    } catch (error) {
         logEvent('inheritance_add_item', {
     item_type: 'secure_phrase',
     item_alias: phrase?.name || 'unknown',
     add_result: 'failure',
     error_message: error?.message || 'unknown'
   });
      showAlert(`Error adding phrase to endowment: ${error.message}`, 'danger');
    } finally {
      setIsAddingToInheritance(false);
    }
  };

  // Handle delete selected phrases
  const handleDeleteSelected = () => {
    if (selectedPhrases.length > 0) {
      setMultipleDeleteModalOpen(true);
    }
  };

  // Confirm delete phrase
  const confirmDeletePhrase = async () => {
    setIsDeleting(true);
    try {
      await deletePhrase(currentPhrase.name, token);
      showAlert('Secure phrase deleted successfully', 'success');
      setDeleteModalOpen(false);
      setCurrentPhrase(null);
      loadPhrases();
    } catch (error) {
      showAlert(`Error deleting secure phrase: ${error.message}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // Confirm delete multiple phrases
  const confirmDeleteMultiple = async () => {
    setIsDeleting(true);
    try {
      const deletePromises = selectedPhrases.map(name =>
        deletePhrase(name, token)
      );

      await Promise.all(deletePromises);
      showAlert('Selected secure phrases deleted successfully', 'success');
      setMultipleDeleteModalOpen(false);
      setSelectedPhrases([]);
      loadPhrases();
    } catch (error) {
      showAlert(`Error deleting secure phrases: ${error.message}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // Confirm add to inheritance
  const handleSavePhrase = async (phraseData, originalName) => {
    setIsSaving(true);
     const action = originalName ? 'update' : 'create';
 const requestedAlias = phraseData.alias;
 let aliasChanged = false;
    try {
      if (!originalName) {
        // For new phrases, ensure alias is unique
        phraseData.alias = await generateUniquePhraseAlias(phraseData.alias, token);
        aliasChanged = phraseData.alias !== requestedAlias;
      }else {
      // For editing, check if the alias has changed
      const originalAlias = removeTxtExtension(originalName);
      if (phraseData.alias !== originalAlias) {
        // Alias changed during editing, ensure new alias is unique
        phraseData.alias = await generateUniquePhraseAlias(phraseData.alias, token);
        aliasChanged = true;
      }
    }

      await savePhrase(phraseData, originalName, token);
      showAlert('Secure phrase saved successfully', 'success');
      setFormModalOpen(false);
      loadPhrases();
         logEvent('vault_item_save', {
     item_type: 'secure_phrase',
     action,
     alias: phraseData.alias,
     alias_changed: aliasChanged,
     save_result: 'success'
   });
      return true;
    } catch (error) {
      showAlert(`Error saving secure phrase: ${error.message}`, 'danger');
         logEvent('vault_item_save', {
     item_type: 'secure_phrase',
     action,
     alias: phraseData.alias,
     alias_changed: aliasChanged,
     save_result: 'failure',
     error_message: error?.message || 'unknown'
   });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle checkbox selection
  const handleSelectPhrase = (name, isSelected, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (isSelected) {
      setSelectedPhrases(prev => [...prev, name]);
    } else {
      setSelectedPhrases(prev => prev.filter(item => item !== name));
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle sort
  const handleSort = (type) => {
    setSortType(type);
    let sortedPhrases = [...phrases];

    switch (type) {
      case 'nameAsc':
        sortedPhrases.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'nameDesc':
        sortedPhrases.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'dateAsc':
        sortedPhrases.sort((a, b) => new Date(a.lastModified) - new Date(b.lastModified));
        break;
      case 'dateDesc':
        sortedPhrases.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
        break;
      default:
        break;
    }

    setPhrases(sortedPhrases);
  };

  // Filter phrases based on search query
  const filteredPhrases = phrases.filter(phrase =>
    removeTxtExtension(phrase.name).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get sort button text
  const getSortButtonText = () => {
    switch (sortType) {
      case 'nameAsc': return 'Name (A-Z)';
      case 'nameDesc': return 'Name (Z-A)';
      case 'dateAsc': return 'Date (Oldest)';
      case 'dateDesc': return 'Date (Newest)';
      default: return 'Sort: None';
    }
  };

  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar toggled={sidebarToggled} setToggled={setSidebarToggled} />

        <div id="content-wrapper" className={`d-flex flex-column ${sidebarToggled ? 'content-toggled' : ''}`}>
          <div id="content">
            <DashboardHeader toggleSidebar={() => setSidebarToggled(!sidebarToggled)} />

            <div className="container-fluid">
              {/* Alert */}
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert(prev => ({ ...prev, show: false }))}
                  dismissible
                  className="mt-3"
                >
                  {alert.message}
                </Alert>
              )}

              <div className="row">
                <div className="col-lg-12">
                  <div className="card shadow mb-4" style={{ height: 'auto', display: 'flex', flexDirection: 'column', padding: '5px' }}>
                    {/* Use the reusable list header component */}
                    <ListHeader
                      title="Secure Phrases"
                      searchQuery={searchQuery}
                      onSearch={handleSearch}
                      sortType={sortType}
                      onSort={handleSort}
                      onRefresh={() => loadPhrases(true)}
                      isLoading={isLoading}
                      searchPlaceholder="Search secure phrases..."
                    />

                    {filteredPhrases.length > 0 && (
                      <div className="select-all-container p-2 border-bottom">
                        <div className="d-flex align-items-center">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="selectAllPhrasesCheckbox"
                              checked={areAllPhrasesSelected()}
                              ref={(input) => {
                                if (input) input.indeterminate = areSomePhrasesSelected();
                              }}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              style={{ width: '18px', height: '18px' }}
                            />
                            <label className="form-check-label ms-2" htmlFor="selectAllPhrasesCheckbox">
                              {selectedPhrases.length > 0
                                ? `${selectedPhrases.length} of ${filteredPhrases.length} selected`
                                : `Select all ${filteredPhrases.length} phrases`
                              }
                            </label>
                          </div>

                          
                        </div>
                      </div>
                    )}

                    <div className="card-body header-sort p-0" style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '5px' }}>
                      <div id="cardBody" style={{ minHeight: '100%', backgroundColor: '#ffffff', color: '#000000', paddingBottom: '25px' }}>
                        <div style={{ paddingBottom: '25px' }}></div>
                        {isLoading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border" role="status" style={{ color: '#00CC5C' }}>
                              <span className="sr-only">Loading...</span>
                            </div>
                          </div>
                        ) : filteredPhrases.length === 0 ? (
                          <div className="empty-state text-center py-5">
                            <i className="fas fa-key fa-3x text-black mb-3"></i>
                            <h5 className="text-black">No Secure Phrases Added Yet</h5>
                            <p className="text-black">Add your first secure phrase to get started.</p>
                            <button
                              className="btn btn-sm mt-2"
                              style={{
                                backgroundColor: '#00CC5C',
                                color: '#FFFFFF',
                                padding: '4px 10px',
                                fontSize: '14px',
                                width: 'auto',
                                display: 'inline-block',
                              }}
                              onClick={handleAddPhrase}
                              id="addNewPhraseBtn"
                            >
                              <i className="fas fa-plus-circle mr-2 " style={{ fontSize: '20px' }}></i>Add New Phrase
                            </button>
                          </div>
                        ) : (
                          // Blockchain phrase items
                          filteredPhrases.map(phrase => (
                            <div
                              key={phrase.id}
                              className="item d-flex align-items-center card-item-container"
                              id={`card-${phrase.id}`}
                              ref={el => cardRefs.current[phrase.id] = el}
                              onClick={(e) => handleCardClick(phrase.id, e)}
                              onDoubleClick={(e) => handleCardDoubleClick(phrase, e)}
                              style={{ cursor: 'pointer', position: 'relative' }}
                            >
                              <div className="item-content d-flex align-items-center flex-grow-1">
                                <div className="item-check" style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${phrase.id}`}
                                    className="form-check-input item-checkbox"
                                    checked={selectedPhrases.includes(phrase.name)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => handleSelectPhrase(phrase.name, e.target.checked, e)}
                                    style={{ width: '18px', height: '18px', margin: '0' }}
                                  />
                                </div>
                                <div className="item-icon" style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <i className="fas fa-file-alt" style={{ color: '#00CC5C', fontSize: '24px' }}></i>
                                </div>
                                <div className="item-name text-truncate" style={{ paddingRight: '10px' }} title={removeTxtExtension(phrase.name)}>
                                  {removeTxtExtension(phrase.name)}
                                </div>
                                <div className="item-date text-right ml-auto" style={{ minWidth: '50px' }}>
                                  {formatItemDate(phrase.lastModified)}
                                </div>
                                <div className="item-size text-right ml-auto" style={{ minWidth: '50px' }}>
                                  {formatFileSize(phrase.size)}
                                </div>
                                <div className="item-size text-right ml-auto" style={{ minWidth: '20px' }}>
                                  <i className="fas fa-ellipsis-v" style={{ color: '#00CC5C' }}></i>
                                </div>
                              </div>

                              {/* Dropdown menu positioned absolutely */}
                              <div
                                ref={el => dropdownRefs.current[phrase.id] = el}
                                className={`dropdown-menu dropdown-menu-right ${openDropdownId === phrase.id ? 'show' : ''}`}
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: '0',
                                  zIndex: 1000,
                                  display: openDropdownId === phrase.id ? 'block' : 'none',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`view-${phrase.id}`}
                                  onClick={(e) => handleViewPhrase(phrase, e)}
                                >
                                  <i className="fas fa-eye fa-sm fa-fw mr-2 text-black-400"></i>View
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`edit-${phrase.id}`}
                                  onClick={(e) => handleEditPhrase(phrase, e)}
                                >
                                  <i className="fas fa-edit fa-sm fa-fw mr-2 text-black-400"></i>Edit
                                </a>
                                {user?.SubscriptionType !== "1" && user?.SubscriptionType !== "3" && (
                                  <InheritanceDropdownItem
                                    onClick={(e) => handleAddToInheritance(phrase, e)}
                                    isLoading={isAddingToInheritance}
                                    defaultText="Add to Endowment"
                                    loadingText="Adding..."
                                  />

                                )}
                                <div className="dropdown-divider"></div>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`delete-${phrase.id}`}
                                  data-action="delete-item"
                                  data-item-name={phrase.name}
                                  data-item-type="phrase"
                                  onClick={(e) => handleDeletePhrase(phrase, e)}
                                >
                                  <i className="fas fa-trash-alt fa-sm fa-fw mr-2 text-black-400"></i>Delete
                                </a>
                                <a className="dropdown-item"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCurrentPhrase(phrase);
                                    setPropertiesModalOpen(true);
                                  }}
                                >
                                  <i className="fas fa-info-circle fa-sm fa-fw mr-2 text-black-400"></i>Properties
                                </a>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Floating Button */}
                  <div id="floatingButtonContainer">
                    {selectedPhrases.length > 0 ? (
                      <button
                        id="deletefloatingButton"
                        className="btn btn-icon"
                        title="Delete"
                        onClick={handleDeleteSelected}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    ) : (
                      <button
                        id="addPhraseButton"
                        className="btn btn-icon-add"
                        title="Add Secure Phrase"
                        style={{ fontSize: '16px' }}
                        onClick={handleAddPhrase}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ViewPhraseModal
        show={viewModalOpen}
        onHide={() => setViewModalOpen(false)}
        phraseData={currentPhrase}
      />

      <PhraseFormModal
        show={formModalOpen}
        onHide={() => setFormModalOpen(false)}
        initialData={isEditing ? currentPhrase : null}
        isEditing={isEditing}
        onSave={handleSavePhrase}
        isLoading={isSaving}
      />

      <DeletePhraseModal
        show={deleteModalOpen}
        onHide={() => setDeleteModalOpen(false)}
        phraseName={currentPhrase?.originalName}
        onConfirm={confirmDeletePhrase}
        isSingle={true}
        isLoading={isDeleting}
      />

      <DeletePhraseModal
        show={multipleDeleteModalOpen}
        onHide={() => setMultipleDeleteModalOpen(false)}
        onConfirm={confirmDeleteMultiple}
        isSingle={false}
        isLoading={isDeleting}
      />

      <PropertiesModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        itemData={currentPhrase}
        itemType="phrase"
      />
    </div>
  );
};

export default Blockchain;